/**
 * usePhotos Hook
 * 
 * Fetches and manages photos with filtering, pagination, and real-time updates.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import type { Photo, PhotoFilters, PhotoSortBy, PhotoWithRelations } from "../types";

interface UsePhotosOptions {
  filters?: PhotoFilters;
  sortBy?: PhotoSortBy;
  limit?: number;
  realtime?: boolean;
}

interface UsePhotosReturn {
  photos: PhotoWithRelations[];
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
  hasMore: boolean;
  totalCount: number;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  deletePhoto: (photoId: string) => Promise<boolean>;
  toggleVisibility: (photoId: string, visible: boolean) => Promise<boolean>;
  toggleFeatured: (photoId: string, featured: boolean) => Promise<boolean>;
}

const PAGE_SIZE = 20;

/**
 * Hook to fetch and manage photos
 */
export const usePhotos = (options: UsePhotosOptions = {}): UsePhotosReturn => {
  const { filters = {}, sortBy = "newest", limit, realtime = false } = options;
  const { user, roles } = useAuth();
  
  const [photos, setPhotos] = useState<PhotoWithRelations[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);

  // Serialize filters to avoid infinite re-renders
  const filtersKey = JSON.stringify(filters);

  /**
   * Build the query with filters
   */
  const buildQuery = useCallback(() => {
    let query = supabase
      .from("files")
      .select(`
        *,
        uploader:profiles!files_uploaded_by_fkey(id, full_name, nickname, avatar_url),
        team:teams(id, name, color),
        weekend:family_weekends(id, name, year),
        challenge:challenges(id, title)
      `, { count: "exact" })
      .eq("category", "photo");

    // Apply filters
    if (filters.weekendId) {
      query = query.eq("weekend_id", filters.weekendId);
    }
    if (filters.userId) {
      query = query.eq("uploaded_by", filters.userId);
    }
    if (filters.teamId) {
      query = query.eq("team_id", filters.teamId);
    }
    if (filters.activityType) {
      query = query.eq("activity_type", filters.activityType);
    }
    if (filters.challengeId) {
      query = query.eq("challenge_id", filters.challengeId);
    }
    if (filters.isFeatured) {
      query = query.eq("is_featured", true);
    }
    if (filters.searchQuery) {
      query = query.or(`description.ilike.%${filters.searchQuery}%,tags.cs.{${filters.searchQuery}}`);
    }

    // Only show visible photos (unless super admin)
    if (!roles.isSuperAdmin) {
      query = query.eq("is_visible", true);
    }

    // Apply sorting
    switch (sortBy) {
      case "oldest":
        query = query.order("created_at", { ascending: true });
        break;
      case "most_liked":
        query = query.order("likes_count", { ascending: false });
        break;
      case "most_commented":
        query = query.order("comments_count", { ascending: false });
        break;
      case "newest":
      default:
        query = query.order("created_at", { ascending: false });
        break;
    }

    return query;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey, sortBy, roles.isSuperAdmin]);

  /**
   * Fetch photos
   */
  const fetchPhotos = useCallback(async (pageNum: number, isRefresh = false) => {
    try {
      if (isRefresh) {
        setIsRefreshing(true);
      } else if (pageNum === 0) {
        setIsLoading(true);
      }
      setError(null);

      const pageSize = limit || PAGE_SIZE;
      const from = pageNum * pageSize;
      const to = from + pageSize - 1;

      const query = buildQuery().range(from, to);
      const { data, error: fetchError, count } = await query;

      if (fetchError) throw fetchError;

      const photosData = (data || []) as unknown as PhotoWithRelations[];

      if (isRefresh || pageNum === 0) {
        setPhotos(photosData);
      } else {
        setPhotos(prev => [...prev, ...photosData]);
      }

      setTotalCount(count || 0);
      setHasMore(photosData.length === pageSize);
      setPage(pageNum);
    } catch (err) {
      console.error("Error fetching photos:", err);
      setError("Kon foto's niet laden");
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [buildQuery, limit]);

  /**
   * Load more photos (pagination)
   */
  const loadMore = useCallback(async () => {
    if (!hasMore || isLoading) return;
    await fetchPhotos(page + 1);
  }, [hasMore, isLoading, page, fetchPhotos]);

  /**
   * Refresh photos
   */
  const refresh = useCallback(async () => {
    await fetchPhotos(0, true);
  }, [fetchPhotos]);

  /**
   * Delete a photo
   */
  const deletePhoto = useCallback(async (photoId: string): Promise<boolean> => {
    try {
      const photo = photos.find(p => p.id === photoId);
      if (!photo) return false;

      // Check permission
      if (photo.uploaded_by !== user?.id && !roles.isSuperAdmin) {
        throw new Error("Geen toestemming om deze foto te verwijderen");
      }

      // Delete from storage first
      if (photo.file_url) {
        const path = photo.file_url.split("/").pop();
        if (path) {
          await supabase.storage.from("family-gallery").remove([path]);
        }
      }

      // Delete from database
      const { error: deleteError } = await supabase
        .from("files")
        .delete()
        .eq("id", photoId);

      if (deleteError) throw deleteError;

      // Update local state
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      setTotalCount(prev => Math.max(0, prev - 1));

      return true;
    } catch (err) {
      console.error("Error deleting photo:", err);
      return false;
    }
  }, [photos, user?.id, roles.isSuperAdmin]);

  /**
   * Toggle photo visibility (super admin only)
   */
  const toggleVisibility = useCallback(async (photoId: string, visible: boolean): Promise<boolean> => {
    if (!roles.isSuperAdmin) return false;

    try {
      const { error: updateError } = await supabase
        .from("files")
        .update({ is_visible: visible, updated_at: new Date().toISOString() })
        .eq("id", photoId);

      if (updateError) throw updateError;

      setPhotos(prev => prev.map(p => 
        p.id === photoId ? { ...p, is_visible: visible } : p
      ));

      return true;
    } catch (err) {
      console.error("Error toggling visibility:", err);
      return false;
    }
  }, [roles.isSuperAdmin]);

  /**
   * Toggle photo featured status (super admin only)
   */
  const toggleFeatured = useCallback(async (photoId: string, featured: boolean): Promise<boolean> => {
    if (!roles.isSuperAdmin) return false;

    try {
      const { error: updateError } = await supabase
        .from("files")
        .update({ is_featured: featured, updated_at: new Date().toISOString() })
        .eq("id", photoId);

      if (updateError) throw updateError;

      setPhotos(prev => prev.map(p => 
        p.id === photoId ? { ...p, is_featured: featured } : p
      ));

      return true;
    } catch (err) {
      console.error("Error toggling featured:", err);
      return false;
    }
  }, [roles.isSuperAdmin]);

  // Initial fetch
  useEffect(() => {
    fetchPhotos(0);
  }, [fetchPhotos]);

  // Realtime subscription
  useEffect(() => {
    if (!realtime) return;

    const channel = supabase
      .channel("photos-realtime")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "files",
          filter: "category=eq.photo",
        },
        () => {
          // Refresh on any change
          fetchPhotos(0, true);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [realtime, fetchPhotos]);

  return {
    photos,
    isLoading,
    isRefreshing,
    error,
    hasMore,
    totalCount,
    loadMore,
    refresh,
    deletePhoto,
    toggleVisibility,
    toggleFeatured,
  };
};

